import { isReference, resolveId } from '@medplum/core'
import type { Reference, Task } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { cn } from '@seen/ui'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { useRead } from '#src/hooks/useRead.ts'

export function HoverTask({
  task: taskOrReference,
  className,
  showOwner,
  showParticipant,
}: {
  task: StandardTask | Reference<Task> | undefined
  className?: string
  showOwner?: boolean
  showParticipant?: boolean
}): JSX.Element {
  const { data } = useRead(
    StandardTask,
    isReference(taskOrReference) ? taskOrReference : undefined,
    {
      query: {
        enabled: !(taskOrReference instanceof StandardTask),
      },
    }
  )
  const task = taskOrReference instanceof StandardTask ? taskOrReference : data

  const taskReference =
    taskOrReference instanceof StandardTask
      ? taskOrReference.reference
      : taskOrReference

  const { data: taskParticipant } = useRead(Participant, task?.participant)

  return (
    <Link
      to={
        taskParticipant
          ? `${taskParticipant.relativeUrl}/tasks/${resolveId(taskReference)}`
          : `/tasks/${resolveId(taskReference)}`
      }
      className={cn(
        'inline-flex font-medium text-foreground decoration-1 underline-offset-[2.5px] hover:underline',
        className
      )}
    >
      <span className="relative top-[-0.03125rem] mr-0.75 inline-flex">
        {task?.state && (
          <span className="h-[1em]">
            <IconTaskState state={task.state} />
          </span>
        )}
        {showOwner && (
          <span className="relative -ml-1 inline-flex">
            <span className="inline-flex">
              {(!task || !!task?.ownerPractitioner) && (
                <span className="inline-flex rounded-full border border-white bg-white">
                  <Avatar profile={task?.ownerPractitioner} size={3.5} />
                </span>
              )}
              {!!task && !task?.ownerPractitioner && (
                <span className="inline-flex rounded-full bg-white">
                  <IconUserPlaceholder className="size-4 p-0" />
                </span>
              )}
            </span>
          </span>
        )}
      </span>
      <RichText
        className="line-clamp-1 max-w-xs truncate text-ellipsis"
        value={task?.title ?? referenceDisplay(taskReference) ?? ''}
        inline
      />
      {showParticipant && task?.participant && (
        <span className="ml-1 inline-flex items-center">
          <span className="inline-flex h-[1em] items-center">
            <ParticipantTaskRowBadge participant={task.participant} />
          </span>
        </span>
      )}
    </Link>
  )
}
